package fr.manyverse.app.presentation.viewmodel.gamemaster

import fr.manyverse.app.domain.model.ActiveAnnouncement
import fr.manyverse.app.domain.model.EventContent
import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.usecase.CancelAnnouncementUseCase
import fr.manyverse.app.domain.usecase.CreateAnnouncementUseCase
import fr.manyverse.app.domain.usecase.GetTableEventsUseCase
import fr.manyverse.app.infrastructure.datastore.UserPreferencesDataStore
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.launchAsyncWithCallback
import kotlin.time.Instant
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val ANNOUNCEMENTS_FETCH_LIMIT = 100

/**
 * Owns the GameMaster announcements: active list, dismissal persistence and creation/cancel.
 * Extracted from GameMasterViewModel; behavior is intentionally identical.
 */
class GameMasterAnnouncementsDelegate(
    private val scope: CoroutineScope,
    private val tableId: String,
    private val currentUserId: String,
    private val createAnnouncementUseCase: CreateAnnouncementUseCase,
    private val cancelAnnouncementUseCase: CancelAnnouncementUseCase,
    private val getTableEventsUseCase: GetTableEventsUseCase,
    private val userPreferencesDataStore: UserPreferencesDataStore,
    private val errorNotifier: UserErrorNotifier,
    private val onAnnouncementCreated: () -> Unit
) {
    private val _announcementState = MutableStateFlow<AsyncUiState<GameEvent>>(AsyncUiState.Idle)
    val announcementState: StateFlow<AsyncUiState<GameEvent>> = _announcementState.asStateFlow()

    private val _activeAnnouncements = MutableStateFlow<List<ActiveAnnouncement>>(emptyList())
    val activeAnnouncements: StateFlow<List<ActiveAnnouncement>> = _activeAnnouncements.asStateFlow()

    private val dismissedAnnouncementIds = mutableSetOf<String>()
    private var latestAnnouncementAt: Instant? = null

    fun loadDismissedAnnouncements() {
        scope.launch {
            val persisted = userPreferencesDataStore.getDismissedAnnouncements(currentUserId, tableId)
            if (persisted.isNotEmpty()) {
                dismissedAnnouncementIds.addAll(persisted)
                // Si load() a déjà émis avant qu'on charge la liste, on filtre rétroactivement
                // les annonces déjà fermées sur d'autres devices.
                _activeAnnouncements.update { current ->
                    current.filter { it.eventId !in persisted }
                }
            }
        }
    }

    private fun persistDismissedAnnouncements() {
        scope.launch {
            userPreferencesDataStore.saveDismissedAnnouncements(
                userId = currentUserId,
                tableId = tableId,
                eventIds = dismissedAnnouncementIds.toSet()
            )
        }
    }

    fun load() {
        scope.launch {
            val since = latestAnnouncementAt
            getTableEventsUseCase(
                tableId, limit = ANNOUNCEMENTS_FETCH_LIMIT, offset = 0,
                eventTypes = listOf(EventType.ANNOUNCEMENT, EventType.ANNOUNCEMENT_CANCELLED),
                since = since
            ).onSuccess { events ->
                if (events.isEmpty()) return@onSuccess
                latestAnnouncementAt = events.maxOf { it.createdAt }

                val cancelledIds = events
                    .filter { it.eventType == EventType.ANNOUNCEMENT_CANCELLED }
                    .mapNotNull { (it.content as? EventContent.AnnouncementCancelled)?.announcementEventId }
                    .toSet()

                if (cancelledIds.isNotEmpty()) {
                    _activeAnnouncements.update { current -> current.filter { it.eventId !in cancelledIds } }
                }

                val existingIds = _activeAnnouncements.value.map { it.eventId }.toSet()
                val toAdd = events
                    .filter {
                        it.eventType == EventType.ANNOUNCEMENT &&
                            it.id !in cancelledIds &&
                            it.id !in dismissedAnnouncementIds &&
                            it.id !in existingIds
                    }
                    .map { event ->
                        val content = event.content as EventContent.Announcement
                        ActiveAnnouncement(
                            eventId = event.id,
                            text = content.text,
                            imageUrl = content.imageUrl,
                            targetParticipantIds = event.targetParticipants,
                            createdAt = event.createdAt
                        )
                    }

                if (toAdd.isNotEmpty()) {
                    _activeAnnouncements.update { it + toAdd }
                }
            }
        }
    }

    fun create(text: String?, image: ImageData?, targetParticipants: List<String>) {
        scope.launchAsyncWithCallback(_announcementState, {
            createAnnouncementUseCase(
                tableId = tableId,
                triggeredBy = currentUserId,
                targetParticipants = targetParticipants,
                text = text,
                image = image
            )
        }) { event ->
            val content = event.content as? EventContent.Announcement
            val newAnnouncement = ActiveAnnouncement(
                eventId = event.id,
                text = content?.text,
                imageUrl = content?.imageUrl,
                targetParticipantIds = targetParticipants,
                createdAt = event.createdAt
            )
            // L'événement Realtime peut arriver avant la réponse HTTP : il est alors déjà dans la liste
            _activeAnnouncements.update { current ->
                if (current.any { it.eventId == event.id }) current else current + newAnnouncement
            }
            onAnnouncementCreated()
        }
    }

    fun cancel(announcementEventId: String) {
        val announcement = _activeAnnouncements.value.find { it.eventId == announcementEventId } ?: return
        scope.launch {
            cancelAnnouncementUseCase(
                tableId = tableId,
                triggeredBy = currentUserId,
                targetParticipants = announcement.targetParticipantIds,
                announcementEventId = announcementEventId
            ).onSuccess {
                if (dismissedAnnouncementIds.add(announcementEventId)) {
                    persistDismissedAnnouncements()
                }
                _activeAnnouncements.update { it.filter { a -> a.eventId != announcementEventId } }
            }.onFailure(errorNotifier::notify)
        }
    }

    fun onRealtimeAnnouncement(event: GameEvent) {
        val content = event.content as? EventContent.Announcement ?: return
        if (event.id in dismissedAnnouncementIds) return
        _activeAnnouncements.update { current ->
            if (current.any { it.eventId == event.id }) return@update current
            current + ActiveAnnouncement(
                eventId = event.id,
                text = content.text,
                imageUrl = content.imageUrl,
                targetParticipantIds = event.targetParticipants,
                createdAt = event.createdAt
            )
        }
        val current = latestAnnouncementAt
        if (current == null || event.createdAt > current) {
            latestAnnouncementAt = event.createdAt
        }
    }

    fun onRealtimeCancelled(event: GameEvent) {
        val content = event.content as? EventContent.AnnouncementCancelled ?: return
        val cancelledId = content.announcementEventId ?: return
        if (dismissedAnnouncementIds.add(cancelledId)) {
            persistDismissedAnnouncements()
        }
        _activeAnnouncements.update { it.filter { a -> a.eventId != cancelledId } }
    }
}
